// The tabs of an agent's page (ADR 0038 §3): a REGISTERED list, so another part of the SDK (or a host) adds a tab
// with `registerAgentTab` instead of a case in the page's JSX. Each tab renders from the agent in the one manifest
// shape (`CatalogAgent`), so the same tabs show a platform persona and a project agent.
import { cn } from '@cortex/ui';
import * as React from 'react';
import { Evaluations } from './Evaluations.tsx';
import { InstructionsTab } from './InstructionsTab.tsx';
import { ModelTab } from './ModelTab.tsx';
import { ToolsTab } from './ToolsTab.tsx';
import { GuardrailsTab, HonestyCard } from './GuardrailsTab.tsx';
import { HarnessSections, HarnessTab, TabLead } from './HarnessTab.tsx';
import { PersonaOverrideTab } from './PersonaOverrideTab.tsx';
import { LongRuns } from './LongRuns.tsx';
import { PlatformRunsBySource, RunsBySource } from './RunsBySource.tsx';
import { UsedIn, type CatalogAgent } from './catalog.tsx';
import { useSearchParams } from 'react-router';
import { useAgentsHost } from './host.tsx';
import { AgentSchema, useSchemaOpen } from './AgentSchema.tsx';

export interface AgentTabContext {
  agent: CatalogAgent;
}

export interface AgentTab {
  /** `?tab=<id>` on the agent's page. */
  id: string;
  label: string;
  /** Position in the tab bar (the built-in tabs are 10, 20 … 70). */
  order: number;
  /** Shown only when this holds (default: always). */
  when?(ctx: AgentTabContext): boolean;
  Render: React.ComponentType<AgentTabContext>;
}

const registry = new Map<string, AgentTab>();

/** Adds (or replaces, by id) a tab of every agent's page. */
export function registerAgentTab(tab: AgentTab): void {
  registry.set(tab.id, tab);
}

/** The registered tabs in their order. */
export function agentTabs(): AgentTab[] {
  return [...registry.values()].sort((a, b) => a.order - b.order || a.id.localeCompare(b.id));
}

const Empty = ({ children }: { children: React.ReactNode }) => (
  <div className="text-sm text-page-muted">{children}</div>
);

function Evals({ agent }: AgentTabContext) {
  const host = useAgentsHost();
  const [search] = useSearchParams();
  const subject = agent.evaluations.subject ?? agent.subject;
  if (!host.evals) return <Empty>Evaluations are not available on this host.</Empty>;
  return (
    <Evaluations
      subject={subject}
      suite={search.get('suite')}
      defaultRole={agent.model.role}
      runRole={search.get('role')}
      tools={agent.tools.map((t) => ({
        name: t.name,
        note: t.source === 'app' ? `app ${t.app}` : t.source === 'builtin' ? 'built-in' : 'platform',
      }))}
    />
  );
}

function Used({ agent }: AgentTabContext) {
  return <UsedIn subject={agent.subject} />;
}

/** Context (tab id `instructions`): what the model sees in each call — its instructions and the chat history (§12–15). */
function ContextTab(ctx: AgentTabContext) {
  return (
    <div className="flex flex-col gap-3">
      <TabLead testId="context-lead">
        Context is what the model sees in each call: its instructions and the chat history. What tools answer
        is set in Tools.
      </TabLead>
      <InstructionsTab {...ctx} />
      <HonestyCard {...ctx} />
      <HarnessSections {...ctx} parts={['history']} />
    </div>
  );
}

/** Tools: what the agent can call, and which calls wait for a person’s OK (§5–10, §20, §31, §33). */
function ToolsWithPermissions(ctx: AgentTabContext) {
  return (
    <div className="flex flex-col gap-3">
      <TabLead testId="tools-lead">
        Tools are what the agent can call — and which calls wait for a person’s OK.
      </TabLead>
      {/* One table: each tool, what it does, where it comes from and its permission (the harness gives the last). */}
      <HarnessSections
        {...ctx}
        parts={['policy', 'other-agents', 'answers']}
        tools={(permissions) => <ToolsTab {...ctx} permissions={permissions} />}
      />
    </div>
  );
}

function Runs(ctx: AgentTabContext) {
  const { agent } = ctx;
  return (
    <div className="flex flex-col gap-3">
      <TabLead testId="runs-lead">
        Done, verified: every check passed · Done, not checked: no check is set · Needs a person: a check
        failed or a call waits for a person’s OK. Under a run that did not end well — what kind of failure it
        was.
      </TabLead>
      {agent.kind === 'project' && (
        <LongRuns
          agent={agent.key}
          mode={String((agent.harness as { mode?: unknown } | null | undefined)?.mode ?? 'interactive')}
        />
      )}
      {agent.kind === 'project' ? (
        <RunsBySource agent={agent.key} />
      ) : (
        <PlatformRunsBySource persona={agent.key} />
      )}
      <HarnessSections {...ctx} parts={['developers']} />
    </div>
  );
}

registerAgentTab({ id: 'instructions', label: 'Context', order: 10, Render: ContextTab });
registerAgentTab({
  id: 'override',
  label: 'Project override',
  order: 15,
  // The old way to change a platform agent in a project: shown only where a project still has one (a project agent
  // made from the platform agent is the way now).
  when: ({ agent }) => agent.kind === 'persona' && !!agent.override,
  Render: PersonaOverrideTab,
});
registerAgentTab({ id: 'model', label: 'Model', order: 20, Render: ModelTab });
registerAgentTab({ id: 'tools', label: 'Tools', order: 30, Render: ToolsWithPermissions });
registerAgentTab({ id: 'guardrails', label: 'Guardrails & Verification', order: 40, Render: GuardrailsTab });
// ADR 0039: the manifest's `harness` section is spread over the tabs where the guide's loop puts each part; its own
// tab (id kept for links) holds how the agent runs and its budgets.
registerAgentTab({ id: 'harness', label: 'Budgets', order: 45, Render: HarnessTab });
registerAgentTab({ id: 'evaluations', label: 'Evaluations', order: 50, Render: Evals });
registerAgentTab({ id: 'used-in', label: 'Used In', order: 60, Render: Used });
registerAgentTab({ id: 'runs', label: 'Runs', order: 70, Render: Runs });

/** The tabs that set the agent up (model + harness, the guide's §1); the others watch it from outside (§36–37). */
const SETUP_TABS = new Set(['instructions', 'override', 'model', 'tools', 'guardrails', 'harness']);

/** The guide's loop (§2, §23–25), each step a link to its tab: what the tabs set up and in which order it runs. */
function LoopLine({
  ids,
  setTab,
  schemaOpen,
  toggleSchema,
}: {
  ids: string[];
  setTab(id: string): void;
  schemaOpen: boolean;
  toggleSchema(): void;
}) {
  const step = (id: string, label: string, model = false) =>
    ids.includes(id) ? (
      <button
        type="button"
        className={cn(
          'rounded-full border-0 px-2 py-0.5 text-xs font-semibold cursor-pointer whitespace-nowrap',
          model ? 'bg-page-bg text-page-text' : 'bg-nav-active/10 text-nav-active',
        )}
        onClick={() => setTab(id)}
      >
        {label}
      </button>
    ) : null;
  return (
    <div
      className="mb-3 inline-flex max-w-full flex-wrap items-center gap-1.5 rounded-lg border border-card-border bg-card-bg px-3 py-1.5 text-xs text-page-muted"
      data-testid="agent-loop"
    >
      <b className="text-[11px] tracking-wider text-page-muted">EACH ANSWER</b>
      {step('instructions', 'Context')}→{step('model', 'Model', true)}→{step('tools', 'Tools')}
      <span>↺ until the answer is final →</span>
      {step('guardrails', 'Guardrails & Verification')}
      <span>·</span>
      {step('harness', 'Budgets')}
      <span>can stop it at any step</span>
      {/* Below: what the harness is, and the schema of it all. */}
      <span className="flex basis-full flex-wrap items-center gap-1.5" data-testid="agent-loop-note">
        <span>Harness = all but the Model ·</span>
        <button
          type="button"
          className="border-0 bg-transparent p-0 text-xs text-nav-active cursor-pointer hover:underline"
          aria-expanded={schemaOpen}
          data-testid="agent-loop-how"
          onClick={toggleSchema}
        >
          {schemaOpen ? 'Hide how it works ▴' : 'How it works ▾'}
        </button>
      </span>
    </div>
  );
}

/**
 * The tab bar and the open tab. `extra` puts tabs of the page itself first (a project agent's editable
 * Overview); `tab`/`setTab` hold the open one (the page keeps it in `?tab=`).
 */
export function AgentTabs({
  ctx,
  tab,
  setTab,
  extra = [],
}: {
  ctx: AgentTabContext;
  tab: string;
  setTab(id: string): void;
  extra?: { id: string; label: string; content: React.ReactNode }[];
}) {
  const tabs = agentTabs().filter((t) => !t.when || t.when(ctx));
  const ids = [...extra.map((e) => e.id), ...tabs.map((t) => t.id)];
  const open = ids.includes(tab) ? tab : ids[0]!;
  const own = extra.find((e) => e.id === open);
  const reg = tabs.find((t) => t.id === open);
  const [schemaOpen, toggleSchema] = useSchemaOpen();
  return (
    // One width for the loop, the tab bar and every tab, as the host's content pages have (max-w-5xl): one right edge,
    // forms not stretched.
    <div className="max-w-5xl" data-testid="agent-tabs">
      <LoopLine ids={ids} setTab={setTab} schemaOpen={schemaOpen} toggleSchema={toggleSchema} />
      {schemaOpen ? <AgentSchema ids={ids} open={open} setTab={setTab} onClose={toggleSchema} /> : null}
      <div role="tablist" className="flex flex-wrap items-end gap-1 border-b border-card-border mb-4">
        {[
          [...extra.map((e) => ({ id: e.id, label: e.label })), ...tabs.filter((t) => SETUP_TABS.has(t.id))],
          tabs.filter((t) => !SETUP_TABS.has(t.id)),
        ].map((group, gi) => (
          // The tabs that set the agent up share a tint; the ones that watch it stand apart, at the right.
          <div
            key={gi}
            className={cn('flex flex-wrap', gi ? 'ml-auto' : 'rounded-t-md bg-nav-active/5 px-1')}
            data-testid={gi ? 'agent-tabs-watch' : 'agent-tabs-setup'}
          >
            {group.map((t) => (
              <button
                key={t.id}
                type="button"
                role="tab"
                aria-selected={t.id === open}
                data-testid={`agent-tab-${t.id}`}
                onClick={() => setTab(t.id)}
                className={
                  'px-3 py-1.5 text-sm border-0 border-b-2 bg-transparent cursor-pointer -mb-px ' +
                  (t.id === open
                    ? 'border-nav-active text-page-text'
                    : 'border-transparent text-page-muted hover:text-page-text')
                }
              >
                {t.label}
              </button>
            ))}
          </div>
        ))}
      </div>
      <div role="tabpanel" data-testid={`agent-tabpanel-${open}`}>
        {own ? own.content : reg ? <reg.Render {...ctx} /> : null}
      </div>
    </div>
  );
}
